import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Eye, ArrowRight, Tag } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const intlLocale = (() => {
    const map = { fr: 'fr-CA', en: 'en-CA', es: 'es-ES', ar: 'ar' };
    return map[i18n.language?.split('-')[0]] || 'fr-CA';
  })();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API}/api/blog/posts?limit=50`);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Pick localized post title/excerpt when an EN version exists
  const localized = (post, field) => {
    const lang = i18n.language?.split('-')[0] || 'fr';
    if (lang !== 'fr' && post[`${field}_en`]) return post[`${field}_en`];
    return post[field];
  };

  const categoryColors = {
    guide: 'bg-blue-100 text-blue-700',
    conseil: 'bg-green-100 text-green-700',
    comparatif: 'bg-purple-100 text-purple-700',
    financement: 'bg-amber-100 text-amber-700',
    entretien: 'bg-teal-100 text-teal-700',
  };

  return (
    <>
      <SEO
        title={t('blog.metaTitle')}
        description={t('blog.metaDescription')}
        url="https://entrepotautomontreal.ca/blog"
      />
      <Navbar />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0c2340] to-[#1a4f85] text-white py-16 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-black mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('blog.heroTitle')}
            </h1>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto">
              {t('blog.heroSubtitle')}
            </p>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="max-w-5xl mx-auto px-4 py-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-slate-500">{t('blog.loading')}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
              <p className="text-slate-500 text-lg">{t('blog.empty')}</p>
              <p className="text-slate-400 mt-2">{t('blog.emptySubtitle')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                    {post.cover_image ? (
                      <img src={post.cover_image.replace('/upload/', '/upload/w_800,h_450,c_fill,q_auto,f_auto/')} alt={localized(post, 'title')} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`blog-cover-${post.slug}`} />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#0c2340] to-[#1a4f85]" />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {post.category && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[post.category] || 'bg-slate-100 text-slate-700'}`}>
                          {post.category}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {post.published_at ? new Date(post.published_at).toLocaleDateString(intlLocale, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    
                    <h2 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors mb-2 line-clamp-2">
                      {localized(post, 'title')}
                    </h2>
                    
                    <p className="text-slate-500 text-sm line-clamp-3 mb-4">
                      {localized(post, 'excerpt')}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        {post.views > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" /> {post.views}
                          </span>
                        )}
                        {post.tags?.slice(0, 2).map((tag) => (
                          <span key={tag} className="flex items-center gap-1">
                            <Tag className="h-3 w-3" /> {tag}
                          </span>
                        ))}
                      </div>
                      <span className="text-blue-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        {t('blog.readArticle')} <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;

